import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';
import { todayLocal } from './lookups';
import type { IconName } from '../components/ui';

/**
 * Recent activity, read straight from the audit log.
 *
 * Every audited table already writes to `audit_log` on insert, update and
 * delete, so this is a record of what actually happened rather than a second
 * log kept in step by hand. Nothing new is stored to build this screen.
 *
 * Admin only — the RLS policy on audit_log is `is_admin()`, so a site engineer
 * asking for it gets nothing back rather than a filtered view. That is
 * deliberate: the log spans payments and bank accounts, which staff must
 * never see.
 *
 * `audit_log.changed` is only a partial record — the whole row on INSERT, but
 * only the fields that moved on UPDATE, so an "approved" event carries no
 * amount and no material name. Rather than parse two different shapes, every
 * surviving audit row is re-joined against the table's CURRENT data: one
 * batched query per table, keyed by row_id. That also means a row edited since
 * the event shows today's figures, not the ones at the moment it happened —
 * the right trade for "what does this say right now", which is what a
 * dashboard is for.
 */

interface AuditRow {
  id: number;
  actor_id: string | null;
  action: string;
  table_name: string;
  row_id: string | null;
  changed: Record<string, unknown> | null;
  created_at: string;
}

export interface ActivityItem {
  id: number;
  at: string;
  /** "Kamesh added a client payment — Advance" — already assembled. */
  text: string;
  who: string;
  /** Project or, for a usage batch, "Project · activity" — whatever names the context. */
  projectName: string | null;
  amountText: string | null;
  icon: IconName;
  tone: 'added' | 'approved' | 'returned' | 'updated' | 'deleted' | 'restored';
}

/** Table label and icon, used whenever a row can't be re-joined (e.g. hard-deleted). */
const SUBJECT: Record<string, { noun: string; icon: IconName }> = {
  client_payments:          { noun: 'a client payment',        icon: 'wallet' },
  purchase_bills:           { noun: 'a supplier bill',         icon: 'filetext' },
  material_purchases:       { noun: 'a material purchase',     icon: 'truck' },
  material_usage_batches:   { noun: 'material used',           icon: 'box' },
  material_usage:           { noun: 'material used',           icon: 'box' },
  subcontractor_labour_batches: { noun: 'attendance',          icon: 'hardhat' },
  subcontractor_labour:     { noun: 'attendance',              icon: 'hardhat' },
  subcontractor_contracts:  { noun: 'a sub-contract',          icon: 'filetext' },
  subcontractor_payments:   { noun: 'a sub-contractor payment', icon: 'wallet' },
  supplier_payments:        { noun: 'a supplier payment',      icon: 'wallet' },
  expenses:                 { noun: 'an expense',              icon: 'wallet' },
  stock_transfer_batches:   { noun: 'a stock transfer',        icon: 'repeat' },
  stock_transfers:          { noun: 'a stock transfer',        icon: 'repeat' },
  fund_transfers:           { noun: 'a fund transfer',         icon: 'transfer' },
  projects:                 { noun: 'a project',               icon: 'briefcase' },
  clients:                  { noun: 'a client',                icon: 'users' },
  suppliers:                { noun: 'a supplier',              icon: 'truck' },
  subcontractors:           { noun: 'a sub-contractor',        icon: 'hardhat' },
  materials:                { noun: 'a material',              icon: 'box' },
  bank_accounts:            { noun: 'a bank account',          icon: 'bank' },
  profiles:                 { noun: 'a team member',           icon: 'shield' },
  staff_project_access:     { noun: 'site access',             icon: 'shield' },
  staff_permissions:        { noun: 'a permission',            icon: 'shield' },
  companies:                { noun: 'company settings',        icon: 'sliders' },
};

/** For an UPDATE, `changed` is `{ field: { from, to } }`. */
type Delta = { from?: unknown; to?: unknown };
const delta = (changed: Record<string, unknown> | null, field: string): Delta | null => {
  const v = changed?.[field];
  return v && typeof v === 'object' && 'to' in (v as object) ? (v as Delta) : null;
};

function describe(r: AuditRow): { verb: string; tone: ActivityItem['tone'] } {
  if (r.action === 'insert') return { verb: 'added', tone: 'added' };
  if (r.action === 'delete') return { verb: 'removed', tone: 'deleted' };

  // Deletes are soft: an UPDATE that stamps deleted_at. Reporting that as
  // "updated" would hide the one change a reader most wants to see.
  const del = delta(r.changed, 'deleted_at');
  if (del) {
    return del.to
      ? { verb: 'deleted', tone: 'deleted' }
      : { verb: 'restored', tone: 'restored' };
  }

  const status = delta(r.changed, 'approval_status');
  if (status?.to === 'approved') return { verb: 'approved', tone: 'approved' };
  if (status?.to === 'rejected') return { verb: 'returned', tone: 'returned' };

  return { verb: 'updated', tone: 'updated' };
}

/** What a table's current row says, once fetched — a sentence fragment and the numbers beside it. */
interface Detail {
  fragment: string;      // "a client payment — advance"
  projectName: string | null;
  amountPaise: number | null;
}

const qty = (n: unknown) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const rel = (v: unknown, key = 'name') => (v as Record<string, string> | null)?.[key] ?? null;

interface Line { quantity: number; materials: { name: string } | null; units: { short_name: string } | null }

/**
 * "40 bags Cement, 2 tonnes Sand, 500 kg Steel" — the actual materials, not
 * just a count. Capped so one hundred-line bill does not turn a dashboard row
 * into a paragraph; the exact bill or batch is still one click away in
 * Approvals or the project ledger for anyone who needs every line.
 */
function listMaterials(lines: Line[], max = 3): string {
  if (!lines.length) return 'no materials';
  const shown = lines
    .slice(0, max)
    .map((l) => `${qty(l.quantity)} ${l.units?.short_name ?? ''} ${l.materials?.name ?? 'material'}`.replace(/\s+/g, ' ').trim());
  const extra = lines.length - shown.length;
  return extra > 0 ? `${shown.join(', ')} and ${extra} more` : shown.join(', ');
}

/**
 * One batched query per table present in the current window, keyed by row_id.
 * Each returns a Map from id to Detail; an id missing from the map (a row that
 * no longer exists) falls back to the plain SUBJECT noun in the caller.
 *
 * material_purchases and material_usage carry their parent id (bill_id /
 * batch_id) precisely so the caller can drop a line that belongs to a header —
 * that header already has its own audit entry and its own Detail below.
 */
async function fetchDetails(byTable: Map<string, Set<string>>) {
  const details = new Map<string, Detail>();
  // bill_id / batch_id per line row, so the caller can suppress lines that
  // belong to a header rather than listing the same delivery twice.
  const parentOf = new Map<string, string | null>();

  const jobs: Array<Promise<void>> = [];

  const ids = (table: string) => Array.from(byTable.get(table) ?? []);

  if (byTable.has('client_payments')) {
    jobs.push((async () => {
      const { data } = await supabase.from('client_payments')
        .select('id, amount_paise, payment_type, projects(name)')
        .in('id', ids('client_payments'));
      for (const r of data ?? []) details.set(r.id, {
        fragment: `a client payment — ${String(r.payment_type).replace('_', ' ')}`,
        projectName: rel(r.projects), amountPaise: r.amount_paise,
      });
    })());
  }

  if (byTable.has('purchase_bills')) {
    jobs.push((async () => {
      const { data } = await supabase.from('purchase_bills')
        .select('id, bill_total_paise, projects(name), suppliers(name), material_purchases(quantity, materials(name), units(short_name))')
        .in('id', ids('purchase_bills'));
      for (const r of data ?? []) {
        const lines = (r.material_purchases ?? []) as Line[];
        const supplier = rel(r.suppliers) ?? 'a supplier';
        details.set(r.id, {
          fragment: `a supplier bill — ${listMaterials(lines)} from ${supplier}`,
          projectName: rel(r.projects), amountPaise: r.bill_total_paise,
        });
      }
    })());
  }

  if (byTable.has('material_purchases')) {
    jobs.push((async () => {
      const { data } = await supabase.from('material_purchases')
        .select('id, bill_id, quantity, amount_paise, materials(name), units(short_name), projects(name)')
        .in('id', ids('material_purchases'));
      for (const r of data ?? []) {
        parentOf.set(r.id, r.bill_id);
        if (r.bill_id) continue;                    // covered by its bill's own entry
        details.set(r.id, {
          fragment: `a material purchase — ${qty(r.quantity)} ${rel(r.units, 'short_name') ?? ''} of ${rel(r.materials) ?? 'material'}`,
          projectName: rel(r.projects), amountPaise: r.amount_paise,
        });
      }
    })());
  }

  if (byTable.has('material_usage_batches')) {
    jobs.push((async () => {
      const { data } = await supabase.from('material_usage_batches')
        .select('id, activity, projects(name), material_usage(quantity, materials(name), units(short_name))')
        .in('id', ids('material_usage_batches'));
      for (const r of data ?? []) {
        const lines = (r.material_usage ?? []) as Line[];
        const activity = r.activity ? ` for ${r.activity}` : '';
        details.set(r.id, {
          fragment: `material used — ${listMaterials(lines)}${activity}`,
          projectName: rel(r.projects), amountPaise: null,
        });
      }
    })());
  }

  if (byTable.has('material_usage')) {
    jobs.push((async () => {
      const { data } = await supabase.from('material_usage')
        .select('id, batch_id, quantity, activity, materials(name), units(short_name), projects(name)')
        .in('id', ids('material_usage'));
      for (const r of data ?? []) {
        parentOf.set(r.id, r.batch_id);
        if (r.batch_id) continue;                   // covered by its batch's own entry
        const activity = r.activity ? ` for ${r.activity}` : '';
        details.set(r.id, {
          fragment: `material used — ${qty(r.quantity)} ${rel(r.units, 'short_name') ?? ''} of ${rel(r.materials) ?? 'material'}${activity}`,
          projectName: rel(r.projects), amountPaise: null,
        });
      }
    })());
  }

  if (byTable.has('subcontractor_labour_batches')) {
    jobs.push((async () => {
      const { data } = await supabase.from('subcontractor_labour_batches')
        .select('id, projects(name), subcontractors(name), subcontractor_labour(worker_count, total_amount_paise, work_types(name))')
        .in('id', ids('subcontractor_labour_batches'));
      for (const r of data ?? []) {
        const lines = (r.subcontractor_labour ?? []) as Array<{ worker_count: number; total_amount_paise: number; work_types: { name: string } | null }>;
        const total = lines.reduce((t, l) => t + Number(l.total_amount_paise), 0);
        const sub = rel(r.subcontractors) ?? 'a sub-contractor';
        const trades = lines.length
          ? lines.map((l) => `${l.worker_count} ${l.work_types?.name ?? 'workers'}`).join(', ')
          : 'no work types';
        details.set(r.id, {
          fragment: `attendance — ${trades} (${sub})`,
          projectName: rel(r.projects), amountPaise: total,
        });
      }
    })());
  }

  if (byTable.has('subcontractor_labour')) {
    jobs.push((async () => {
      const { data } = await supabase.from('subcontractor_labour')
        .select('id, batch_id, worker_count, total_amount_paise, subcontractors(name), work_types(name), projects(name)')
        .in('id', ids('subcontractor_labour'));
      for (const r of data ?? []) {
        parentOf.set(r.id, r.batch_id);
        if (r.batch_id) continue;                   // covered by its visit's own entry
        details.set(r.id, {
          fragment: `attendance — ${r.worker_count} × ${rel(r.work_types) ?? 'workers'} (${rel(r.subcontractors) ?? 'sub-contractor'})`,
          projectName: rel(r.projects), amountPaise: r.total_amount_paise,
        });
      }
    })());
  }

  if (byTable.has('expenses')) {
    jobs.push((async () => {
      const { data } = await supabase.from('expenses')
        .select('id, title, amount_paise, projects(name)')
        .in('id', ids('expenses'));
      for (const r of data ?? []) details.set(r.id, {
        fragment: `an expense — ${r.title}`,
        projectName: rel(r.projects), amountPaise: r.amount_paise,
      });
    })());
  }

  if (byTable.has('stock_transfer_batches')) {
    jobs.push((async () => {
      const { data } = await supabase.from('stock_transfer_batches')
        .select('id, to_project:projects!stock_transfer_batches_to_project_id_fkey(name), stock_transfers(quantity, materials(name), units(short_name))')
        .in('id', ids('stock_transfer_batches'));
      for (const r of data ?? []) {
        const lines = (r.stock_transfers ?? []) as Line[];
        details.set(r.id, {
          fragment: `a stock transfer — ${listMaterials(lines)} to ${rel(r.to_project) ?? 'another site'}`,
          projectName: rel(r.to_project), amountPaise: null,
        });
      }
    })());
  }

  if (byTable.has('stock_transfers')) {
    jobs.push((async () => {
      const { data } = await supabase.from('stock_transfers')
        .select('id, batch_id, quantity, materials(name), units(short_name), to_project:projects!stock_transfers_to_project_id_fkey(name)')
        .in('id', ids('stock_transfers'));
      for (const r of data ?? []) {
        parentOf.set(r.id, r.batch_id);
        if (r.batch_id) continue;                   // covered by its trip's own entry
        details.set(r.id, {
          fragment: `a stock transfer — ${qty(r.quantity)} ${rel(r.units, 'short_name') ?? ''} of ${rel(r.materials) ?? 'material'} to ${rel(r.to_project) ?? 'another site'}`,
          projectName: rel(r.to_project), amountPaise: null,
        });
      }
    })());
  }

  if (byTable.has('fund_transfers')) {
    jobs.push((async () => {
      const { data } = await supabase.from('fund_transfers')
        .select('id, amount_paise, from_account:bank_accounts!fund_transfers_from_account_id_fkey(bank_name), to_account:bank_accounts!fund_transfers_to_account_id_fkey(bank_name)')
        .in('id', ids('fund_transfers'));
      for (const r of data ?? []) details.set(r.id, {
        fragment: `a fund transfer — ${rel(r.from_account, 'bank_name') ?? '?'} → ${rel(r.to_account, 'bank_name') ?? '?'}`,
        projectName: null, amountPaise: r.amount_paise,
      });
    })());
  }

  if (byTable.has('supplier_payments')) {
    jobs.push((async () => {
      const { data } = await supabase.from('supplier_payments')
        .select('id, amount_paise, suppliers(name), projects(name)')
        .in('id', ids('supplier_payments'));
      for (const r of data ?? []) details.set(r.id, {
        fragment: `a supplier payment — ${rel(r.suppliers) ?? 'a supplier'}`,
        projectName: rel(r.projects), amountPaise: r.amount_paise,
      });
    })());
  }

  if (byTable.has('subcontractor_payments')) {
    jobs.push((async () => {
      const { data } = await supabase.from('subcontractor_payments')
        .select('id, amount_paise, subcontractors(name), projects(name)')
        .in('id', ids('subcontractor_payments'));
      for (const r of data ?? []) details.set(r.id, {
        fragment: `a sub-contractor payment — ${rel(r.subcontractors) ?? 'a sub-contractor'}`,
        projectName: rel(r.projects), amountPaise: r.amount_paise,
      });
    })());
  }

  // Master-data tables: name only, never a field-by-field diff — an edit to
  // twelve columns would otherwise read as noise rather than as news. Written
  // out per table rather than through a generic helper: the generated
  // Supabase client only accepts literal table names and literal select
  // strings, not ones assembled at runtime.
  if (byTable.has('projects')) {
    jobs.push((async () => {
      const { data } = await supabase.from('projects').select('id, name').in('id', ids('projects'));
      for (const r of data ?? []) details.set(r.id, { fragment: `a project — ${r.name}`, projectName: null, amountPaise: null });
    })());
  }
  if (byTable.has('clients')) {
    jobs.push((async () => {
      const { data } = await supabase.from('clients').select('id, name').in('id', ids('clients'));
      for (const r of data ?? []) details.set(r.id, { fragment: `a client — ${r.name}`, projectName: null, amountPaise: null });
    })());
  }
  if (byTable.has('suppliers')) {
    jobs.push((async () => {
      const { data } = await supabase.from('suppliers').select('id, name').in('id', ids('suppliers'));
      for (const r of data ?? []) details.set(r.id, { fragment: `a supplier — ${r.name}`, projectName: null, amountPaise: null });
    })());
  }
  if (byTable.has('subcontractors')) {
    jobs.push((async () => {
      const { data } = await supabase.from('subcontractors').select('id, name').in('id', ids('subcontractors'));
      for (const r of data ?? []) details.set(r.id, { fragment: `a sub-contractor — ${r.name}`, projectName: null, amountPaise: null });
    })());
  }
  if (byTable.has('materials')) {
    jobs.push((async () => {
      const { data } = await supabase.from('materials').select('id, name').in('id', ids('materials'));
      for (const r of data ?? []) details.set(r.id, { fragment: `a material — ${r.name}`, projectName: null, amountPaise: null });
    })());
  }
  if (byTable.has('bank_accounts')) {
    jobs.push((async () => {
      const { data } = await supabase.from('bank_accounts').select('id, bank_name').in('id', ids('bank_accounts'));
      for (const r of data ?? []) details.set(r.id, { fragment: `a bank account — ${r.bank_name}`, projectName: null, amountPaise: null });
    })());
  }
  if (byTable.has('profiles')) {
    jobs.push((async () => {
      const { data } = await supabase.from('profiles').select('id, full_name').in('id', ids('profiles'));
      for (const r of data ?? []) details.set(r.id, { fragment: `a team member — ${r.full_name}`, projectName: null, amountPaise: null });
    })());
  }

  await Promise.all(jobs);
  return { details, parentOf };
}

interface ActivityPage {
  items: ActivityItem[];
  /** The audit_log id to page from next, or null when there is nothing older. */
  nextCursor: number | null;
}

/**
 * The instant boundaries of one IST calendar day, as UTC timestamps Postgres
 * can compare `created_at` against directly.
 *
 * `audit_log.created_at` is a timestamptz; a bare `YYYY-MM-DD` compared to it
 * would be read as UTC midnight, which is 5:30am in India — a day filter would
 * then quietly straddle two working days. Anchoring the parse to `+05:30`
 * fixes the instant regardless of the browser's own timezone, and IST has no
 * daylight saving to complicate adding 24 hours.
 */
function istDayBounds(dateStr: string): { start: string; end: string } {
  const startMs = new Date(`${dateStr}T00:00:00+05:30`).getTime();
  return { start: new Date(startMs).toISOString(), end: new Date(startMs + 86_400_000).toISOString() };
}

export type RangePreset = 'today' | '24h' | '7d' | '30d' | 'all' | 'custom';

/** `null` on either end means unbounded in that direction. */
export interface ActivityRange {
  start: string | null;
  end: string | null;
}

/**
 * Turns a preset — or an explicit From/To pair for "custom" — into the
 * instants `fetchActivityPage` filters on.
 *
 * Today is a calendar day; 24H/7D/30D are rolling windows ending now, the way
 * every dashboard that offers this exact button row means them — not
 * calendar-aligned, and with no upper bound, so an item added a second ago
 * still shows without the filter needing to know "now" again on every render.
 */
export function presetRange(preset: RangePreset, custom?: { from: string; to: string }): ActivityRange {
  const rollingFrom = (days: number): ActivityRange => ({
    start: new Date(Date.now() - days * 86_400_000).toISOString(), end: null,
  });
  switch (preset) {
    case 'today': { const { start, end } = istDayBounds(todayLocal()); return { start, end }; }
    case '24h': return rollingFrom(1);
    case '7d': return rollingFrom(7);
    case '30d': return rollingFrom(30);
    case 'custom':
      if (!custom) return { start: null, end: null };
      return { start: istDayBounds(custom.from).start, end: istDayBounds(custom.to).end };
    case 'all':
    default:
      return { start: null, end: null };
  }
}

/**
 * One page of activity, oldest boundary optional, time range optional.
 *
 * Over-fetches from `audit_log` because a bulk bill or batch collapses several
 * raw rows into a single item, so the raw window needs padding to still fill
 * `pageSize` real items after that collapse. The cursor returned is the id of
 * the last raw row actually examined — never the last *item* produced — so a
 * page whose collapsing ran deep still resumes exactly where it left off,
 * with nothing skipped and nothing repeated.
 */
async function fetchActivityPage(
  before: number | null, pageSize: number, range: ActivityRange = { start: null, end: null },
): Promise<ActivityPage> {
  const overFetch = Math.max(pageSize * 4, 40);

  let query = supabase
    .from('audit_log')
    .select('id, actor_id, action, table_name, row_id, changed, created_at')
    .order('id', { ascending: false })
    .limit(overFetch);
  if (before !== null) query = query.lt('id', before);
  if (range.start) query = query.gte('created_at', range.start);
  if (range.end) query = query.lt('created_at', range.end);

  const { data: raw, error } = await query;
  if (error) throw new Error(error.message);
  const rows = raw as AuditRow[];

  const people = await supabase.from('profiles').select('id, full_name');
  const nameOf = new Map((people.data ?? []).map((p) => [p.id, p.full_name]));

  const byTable = new Map<string, Set<string>>();
  for (const r of rows) {
    if (!r.row_id) continue;
    if (!byTable.has(r.table_name)) byTable.set(r.table_name, new Set());
    byTable.get(r.table_name)!.add(r.row_id);
  }

  const { details, parentOf } = await fetchDetails(byTable);

  const items: ActivityItem[] = [];
  let lastRawId: number | null = null;
  let stoppedEarly = false;

  for (const r of rows) {
    lastRawId = r.id;

    // A material_purchases, material_usage or stock_transfers line that
    // belongs to a bill, a batch or a trip is not independent news — that
    // header already produced its own entry, and listing the line too would
    // repeat the same delivery.
    if ((r.table_name === 'material_purchases' || r.table_name === 'material_usage'
         || r.table_name === 'stock_transfers' || r.table_name === 'subcontractor_labour')
        && r.row_id && parentOf.get(r.row_id)) continue;

    const subject = SUBJECT[r.table_name] ?? { noun: r.table_name.replace(/_/g, ' '), icon: 'clipboard' as IconName };
    const { verb, tone } = describe(r);
    const who = (r.actor_id && nameOf.get(r.actor_id)) || 'Someone';
    const found = r.row_id ? details.get(r.row_id) : undefined;

    items.push({
      id: r.id,
      at: r.created_at,
      text: found ? `${who} ${verb} ${found.fragment}` : `${who} ${verb} ${subject.noun}`,
      who,
      projectName: found?.projectName ?? null,
      amountText: found?.amountPaise != null ? formatMoney(found.amountPaise) : null,
      icon: subject.icon,
      tone,
    });
    if (items.length >= pageSize) { stoppedEarly = true; break; }
  }

  // Stopped before the window ran out: definitely more below the cursor. Ran
  // the whole window and it came back full: there may be more beyond it too —
  // only a window smaller than requested proves the log truly ends here.
  const hasMore = stoppedEarly || rows.length === overFetch;
  return { items, nextCursor: hasMore ? lastRawId : null };
}

/** The dashboard teaser — a fixed, un-paginated handful of the newest items. */
export function useRecentActivity(limit = 12) {
  const actor = useSession((s) => s.actor);

  return useQuery({
    queryKey: ['activity', actor?.companyId ?? '', limit],
    enabled: !!actor,
    queryFn: async () => (await fetchActivityPage(null, limit)).items,
  });
}

/**
 * The full /activity screen — one page of `pageSize` at a time.
 *
 * `before` and `day` are owned by the component: paging and the date filter
 * are just different query keys, so switching either is a normal refetch
 * rather than special-cased state inside this hook.
 */
export function useActivityPage(before: number | null, pageSize: number, range: ActivityRange) {
  const actor = useSession((s) => s.actor);

  return useQuery({
    queryKey: ['activity-page', actor?.companyId ?? '', before, pageSize, range.start, range.end],
    enabled: !!actor,
    queryFn: () => fetchActivityPage(before, pageSize, range),
  });
}

/** "24 Aug 2026, 3:45 pm" — what actually happened, not a moving estimate. */
export function exactWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

/** "just now", "20 minutes ago", "3 days ago" — kept as the hover title, so
 *  "how long ago" is still one glance away without being the headline figure. */
export function timeAgo(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
